import { useState, useEffect } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { motion, AnimatePresence } from 'framer-motion'

interface Props {
  token: string | null
  signature: string | null
  expiresAt: number | null
  /** QR edge in px; the entrance check-in screen needs it readable from the door. */
  size?: number
}

export default function QrDisplay({ token, signature, expiresAt, size = 180 }: Props) {
  const [secondsLeft, setSecondsLeft] = useState<number>(0)

  useEffect(() => {
    if (!expiresAt) {
      setSecondsLeft(0)
      return
    }
    const tick = () => {
      const diff = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000))
      setSecondsLeft(diff)
    }
    tick()
    const interval = setInterval(tick, 500)
    return () => clearInterval(interval)
  }, [expiresAt])

  const isExpired = secondsLeft === 0
  const urgency = secondsLeft <= 8

  // Build the QR data payload the mobile app expects: { token, signature }
  const qrData = token && signature
    ? JSON.stringify({ token, signature })
    : null

  return (
    <div className="flex flex-col items-center gap-4">
      {/* QR Code */}
      <div
        className="relative p-4 rounded-2xl transition-all duration-300"
        style={{
          background: '#ffffff',
          boxShadow: isExpired
            ? 'none'
            : urgency
            ? '0 0 30px rgba(239,68,68,0.4)'
            : '0 0 30px rgba(201,58,48,0.25)',
          opacity: isExpired ? 0.3 : 1,
        }}
      >
        <AnimatePresence mode="wait">
          {qrData && !isExpired ? (
            <motion.div
              key={token}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <QRCodeSVG
                value={qrData}
                size={size}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
              />
            </motion.div>
          ) : (
            <motion.div
              key="placeholder"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-center"
              style={{ width: size, height: size }}
            >
              <p className="text-gray-400 text-sm text-center">
                {isExpired ? 'Refreshing…' : 'Generating…'}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Countdown */}
      {token && (
        <div className="flex items-center gap-2">
          <div
            className="text-3xl font-black tabular-nums"
            style={{ color: urgency ? '#FF453A' : '#C93A30' }}
          >
            {secondsLeft}s
          </div>

          {/* Countdown arc */}
          <svg width="32" height="32" viewBox="0 0 32 32" className="-rotate-90">
            <circle cx="16" cy="16" r="14" fill="none" stroke="#ffffff11" strokeWidth="3" />
            <motion.circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke={urgency ? '#FF453A' : '#C93A30'}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 14}`}
              strokeDashoffset={`${2 * Math.PI * 14 * (1 - secondsLeft / 30)}`}
              style={{ transition: 'stroke-dashoffset 0.5s linear' }}
            />
          </svg>
        </div>
      )}

      <p className="text-white/35 text-sm text-center">
        Scan to check in
      </p>
    </div>
  )
}
